import { Fragment, useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";

import {
  Box,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useGetOrdersQuery } from "api/apiSlice";
import LazyImageWithSkeleton from "components/LazyImageWithSkeleton";

import OrderDetails from "./OrderDetails";
import { useSSE } from "contexts/SSEContext";

const Orders = () => {
  const { data, error, isLoading, refetch } = useGetOrdersQuery({
    refetchOnMountOrArgChange: true,
  });
  const theme = useTheme();
  const { orderId } = useParams();
  const navigate = useNavigate();

  const { orderProgressMap } = useSSE();

  const [orderDetailsOpen, setorderDetailsOpen] = useState(false);
  const [order, setorder] = useState(-1);

  // const [ordersProgress, setOrdersProgress] = useState({});

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (orderId && data?.data) {
      const foundOrder = data.data.find((o) => o.uuid === orderId);
      if (foundOrder) {
        console.log(foundOrder);
        setorder(foundOrder);
        setorderDetailsOpen(true);
      }
    }
  }, [orderId, data]);

  // useEffect(() => {
  //   if (!orderDetailsOpen) {
  //     setorder(null);
  //     navigate("/orders");
  //   }
  // }, [orderDetailsOpen]);

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.toString()}</div>;

  const handleOpenDialog = (ord, progress) => {
    if (progress !== 100) return false;

    setorderDetailsOpen(true);
    setorder(ord);
    navigate(`/orders/${ord.uuid}`);
  };
  const handleCloseDialog = () => {
    setorderDetailsOpen(false);
    setorder(-1);
    navigate("/orders"); // remove the orderId from the URL
  };

  return (
    <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 0.5, sm: 1, md: 1 }}
        useFlexGap
        sx={{
          flexWrap: "wrap",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {data.data
          .filter((order) => !order.uuid.includes("_upscale"))
          .map((ord, index) => {
            const progress = orderProgressMap[ord.uuid] ?? {
                overall_progress: ord.progress,
                training_finished: false,
                training_progress: 0,
              } ?? {
                overall_progress: 0,
                training_finished: false,
                training_progress: 0,
              };
            return (
              <Box
                key={index}
                sx={{
                  transition: "transform 0.2s",

                  "&:hover": {
                    transform: "scale(1.03)",
                    cursor:
                      progress.overall_progress !== 100 ? "wait" : "pointer",
                  },
                }}
                onClick={() => handleOpenDialog(ord, progress.overall_progress)}
              >
                <OrderTile
                  img={ord.thumb}
                  status={ord.status}
                  orderProgress={progress}
                  refetch={refetch}
                />
              </Box>
            );
          })}
      </Stack>
      {order !== -1 && (
        <OrderDetails
          isOpen={orderDetailsOpen}
          setIsOpen={setorderDetailsOpen}
          setIsClose={handleCloseDialog}
          order={order}
        />
      )}
    </>
  );
};

export default Orders;

const OrderTile = ({ img, status, orderProgress, refetch }) => {
  const theme = useTheme();

  const [imgVersion, setImgVersion] = useState(0);
  const prevProgress = useRef(orderProgress.overall_progress);

  useEffect(() => {
    // Detect transition from <100 to >=100
    if (prevProgress.current < 100 && orderProgress.overall_progress >= 100) {
      setImgVersion((v) => v + 1);
      refetch();
    }
    prevProgress.current = orderProgress.overall_progress;
  }, [orderProgress]);

  // Append version as a cache-busting query param
  const imgSrc = imgVersion > 0 ? `${img}?v=${imgVersion}` : img;

  return (
    <Fragment>
      <Box
        sx={{
          position: "relative",
          width: 300,
          borderRadius: "15px",
          overflow: "hidden",
        }}
      >
        <LazyImageWithSkeleton
          src={imgSrc}
          alt="thumb"
          width="300px"
          style={{ borderRadius: "15px", display: "block" }}
        />
        {/* Progress bar overlay */}
        {status === 0 && orderProgress.overall_progress < 100 && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              bottom: 0,
              width: "100%",
              bgcolor: "rgba(107, 107, 107, 0.6)", // optional: semi-transparent background
              px: 2,
              py: 2,
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                width: "80%",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box sx={{ width: "100%" }}>
                <LinearProgress
                  variant="determinate"
                  value={Number(orderProgress.overall_progress)}
                  sx={{
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: theme.palette.yellows[400],
                    },
                  }}
                />
              </Box>
            </Box>
            <Typography
              variant="body2"
              sx={{ fontSize: "16px", color: "#fff" }}
            >
              {`${Math.round(orderProgress.overall_progress)}%`}
            </Typography>
            {orderProgress.overall_progress === 0 ? (
              <Typography sx={{ color: "#fff" }}>preparing data</Typography>
            ) : !orderProgress.training_finished ? (
              <Typography sx={{ color: "#fff" }}> training avatar</Typography>
            ) : (
              <Typography sx={{ color: "#fff" }}>generating images</Typography>
            )}
          </Box>
        )}
      </Box>
    </Fragment>
  );
};
